import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _regeneratorRuntime from '@babel/runtime/helpers/esm/regeneratorRuntime';
import _asyncToGenerator from '@babel/runtime/helpers/esm/asyncToGenerator';
import _classCallCheck from '@babel/runtime/helpers/esm/classCallCheck';
import _createClass from '@babel/runtime/helpers/esm/createClass';
import _classPrivateFieldLooseBase from '@babel/runtime/helpers/esm/classPrivateFieldLooseBase';
import _classPrivateFieldLooseKey from '@babel/runtime/helpers/esm/classPrivateFieldLooseKey';
import { v4 } from 'uuid';
import { ContentType } from '@dtvgo/constants';

/**
 * Class that manages the heartbeat and sends the requests to the backend service for
 * the playback experience
 */
var _heartbeatTimeMilliseconds = /*#__PURE__*/_classPrivateFieldLooseKey("heartbeatTimeMilliseconds");
var _deviceId = /*#__PURE__*/_classPrivateFieldLooseKey("deviceId");
var _heartbeatIntervalId = /*#__PURE__*/_classPrivateFieldLooseKey("heartbeatIntervalId");
var _sessionId = /*#__PURE__*/_classPrivateFieldLooseKey("sessionId");
var _playbackId = /*#__PURE__*/_classPrivateFieldLooseKey("playbackId");
var _progress = /*#__PURE__*/_classPrivateFieldLooseKey("progress");
var _duration = /*#__PURE__*/_classPrivateFieldLooseKey("duration");
var _content = /*#__PURE__*/_classPrivateFieldLooseKey("content");
var _isStreamingActionSent = /*#__PURE__*/_classPrivateFieldLooseKey("isStreamingActionSent");
var _useStreamingV = /*#__PURE__*/_classPrivateFieldLooseKey("useStreamingV4");
var _getStreamedTime = /*#__PURE__*/_classPrivateFieldLooseKey("getStreamedTime");
var _backendService = /*#__PURE__*/_classPrivateFieldLooseKey("backendService");
var _stopPromise = /*#__PURE__*/_classPrivateFieldLooseKey("stopPromise");
var _getContentType = /*#__PURE__*/_classPrivateFieldLooseKey("getContentType");
var _getDuration = /*#__PURE__*/_classPrivateFieldLooseKey("getDuration");
var _getPayload = /*#__PURE__*/_classPrivateFieldLooseKey("getPayload");
var _getPayloadV = /*#__PURE__*/_classPrivateFieldLooseKey("getPayloadV4");
var _send = /*#__PURE__*/_classPrivateFieldLooseKey("send");
var _sendStop = /*#__PURE__*/_classPrivateFieldLooseKey("sendStop");
var PlaybackHeartbeatController = /*#__PURE__*/function () {
  /**
   * Creates a new instance of the class.
   * @constructor
   * @param {object} options - The options object.
   * @param {object} options.backendService - The backend service.
   * @param {number} options.heartbeatTimeInSeconds - The heartbeat time in seconds.
   * @param {string} options.deviceId - The device ID.
   * @param {() => number} options.getStreamedTime - A function to get the streamed time.
   */
  function PlaybackHeartbeatController(_ref) {
    var backendService = _ref.backendService,
      heartbeatTimeInSeconds = _ref.heartbeatTimeInSeconds,
      deviceId = _ref.deviceId,
      getStreamedTime = _ref.getStreamedTime,
      useStreamingV4 = _ref.useStreamingV4;
    _classCallCheck(this, PlaybackHeartbeatController);
    Object.defineProperty(this, _sendStop, {
      value: _sendStop2
    });
    Object.defineProperty(this, _send, {
      value: _send2
    });
    Object.defineProperty(this, _getPayloadV, {
      value: _getPayloadV2
    });
    /**
     * Returns the payload used in the requests to the backend service.
     * @returns {object}
     */
    Object.defineProperty(this, _getPayload, {
      value: _getPayload2
    });
    Object.defineProperty(this, _getDuration, {
      value: _getDuration2
    });
    Object.defineProperty(this, _getContentType, {
      value: _getContentType2
    });
    /** @type {number} */
    Object.defineProperty(this, _heartbeatTimeMilliseconds, {
      writable: true,
      value: undefined
    });
    /** @type {string} */
    Object.defineProperty(this, _deviceId, {
      writable: true,
      value: undefined
    });
    Object.defineProperty(this, _heartbeatIntervalId, {
      writable: true,
      value: undefined
    });
    /** @type {string} */
    Object.defineProperty(this, _sessionId, {
      writable: true,
      value: undefined
    });
    /** @type {string} */
    Object.defineProperty(this, _playbackId, {
      writable: true,
      value: undefined
    });
    /** @type {number} */
    Object.defineProperty(this, _progress, {
      writable: true,
      value: 0
    });
    /** @type {number} */
    Object.defineProperty(this, _duration, {
      writable: true,
      value: undefined
    });
    Object.defineProperty(this, _content, {
      writable: true,
      value: undefined
    });
    /** @type {boolean} */
    Object.defineProperty(this, _isStreamingActionSent, {
      writable: true,
      value: false
    });
    /** @type {boolean} */
    Object.defineProperty(this, _useStreamingV, {
      writable: true,
      value: false
    });
    /** @type {() => number} */
    Object.defineProperty(this, _getStreamedTime, {
      writable: true,
      value: undefined
    });
    Object.defineProperty(this, _backendService, {
      writable: true,
      value: undefined
    });
    /** @type {Promise<void>} */
    Object.defineProperty(this, _stopPromise, {
      writable: true,
      value: undefined
    });
    _classPrivateFieldLooseBase(this, _heartbeatTimeMilliseconds)[_heartbeatTimeMilliseconds] = heartbeatTimeInSeconds * 1000;
    _classPrivateFieldLooseBase(this, _deviceId)[_deviceId] = deviceId;
    _classPrivateFieldLooseBase(this, _getStreamedTime)[_getStreamedTime] = getStreamedTime;
    _classPrivateFieldLooseBase(this, _backendService)[_backendService] = backendService;
    _classPrivateFieldLooseBase(this, _useStreamingV)[_useStreamingV] = useStreamingV4;
  }

  /**
   * Starts the playback heartbeat with the given parameters.
   *
   * @param {object} options - The options for starting the playback.
   * @param {string} options.playbackId - The playback ID.
   * @param {Asset} options.asset - The asset being played.
   * @param {Schedule} [options.schedule] - The schedule for the playback.
   * @param {boolean} options.isLive - Indicates if the playback is live.
   */
  _createClass(PlaybackHeartbeatController, [{
    key: "start",
    value: function () {
      var _start = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(_ref2) {
        var playbackId, asset, schedule, isLive;
        return _regeneratorRuntime().wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              playbackId = _ref2.playbackId, asset = _ref2.asset, schedule = _ref2.schedule, isLive = _ref2.isLive;
              if (_classPrivateFieldLooseBase(this, _useStreamingV)[_useStreamingV]) {
                _classPrivateFieldLooseBase(this, _isStreamingActionSent)[_isStreamingActionSent] = false;
              }
              if (!(_classPrivateFieldLooseBase(this, _playbackId)[_playbackId] && _classPrivateFieldLooseBase(this, _playbackId)[_playbackId] !== playbackId)) {
                _context.next = 5;
                break;
              }
              _context.next = 5;
              return this.stop();
            case 5:
              if (!(_classPrivateFieldLooseBase(this, _playbackId)[_playbackId] === playbackId && _classPrivateFieldLooseBase(this, _heartbeatIntervalId)[_heartbeatIntervalId])) {
                _context.next = 7;
                break;
              }
              return _context.abrupt("return");
            case 7:
              this.setContent({
                asset: asset,
                schedule: schedule,
                isLive: isLive
              });
              _classPrivateFieldLooseBase(this, _playbackId)[_playbackId] = playbackId;
              _classPrivateFieldLooseBase(this, _sessionId)[_sessionId] = v4();
              _classPrivateFieldLooseBase(this, _heartbeatIntervalId)[_heartbeatIntervalId] = setInterval(_classPrivateFieldLooseBase(this, _send)[_send].bind(this), _classPrivateFieldLooseBase(this, _heartbeatTimeMilliseconds)[_heartbeatTimeMilliseconds]);
            case 11:
            case "end":
              return _context.stop();
          }
        }, _callee, this);
      }));
      function start(_x) {
        return _start.apply(this, arguments);
      }
      return start;
    }()
    /**
     * Stops the heartbeat and send the stop request to the backend service
     * @return {Promise<void>} A Promise that resolves when the heartbeat is stopped.
     */
  }, {
    key: "stop",
    value: function stop() {
      var _this = this;
      if (_classPrivateFieldLooseBase(this, _heartbeatIntervalId)[_heartbeatIntervalId]) {
        clearInterval(_classPrivateFieldLooseBase(this, _heartbeatIntervalId)[_heartbeatIntervalId]);
        _classPrivateFieldLooseBase(this, _heartbeatIntervalId)[_heartbeatIntervalId] = undefined;
      }
      if (_classPrivateFieldLooseBase(this, _playbackId)[_playbackId] && _classPrivateFieldLooseBase(this, _sessionId)[_sessionId] && _classPrivateFieldLooseBase(this, _content)[_content]) {
        _classPrivateFieldLooseBase(this, _stopPromise)[_stopPromise] = _classPrivateFieldLooseBase(this, _sendStop)[_sendStop]();
        _classPrivateFieldLooseBase(this, _playbackId)[_playbackId] = undefined;
        _classPrivateFieldLooseBase(this, _sessionId)[_sessionId] = undefined;
        _classPrivateFieldLooseBase(this, _content)[_content] = undefined;
        return _classPrivateFieldLooseBase(this, _stopPromise)[_stopPromise]["finally"](function () {
          _classPrivateFieldLooseBase(_this, _stopPromise)[_stopPromise] = undefined;
        });
      }
      if (_classPrivateFieldLooseBase(this, _stopPromise)[_stopPromise] !== undefined) return _classPrivateFieldLooseBase(this, _stopPromise)[_stopPromise];
      return Promise.resolve();
    }

    /**
     * Sets the asset and the current schedule for the heartbeat.
     *
     * @param {object} content - The content to set.
     * @param {Asset} content.asset - The asset to set.
     * @param {Schedule} [content.schedule] - The schedule to set.
     * @param {boolean} content.isLive - Indicates if the content is live.
     */
  }, {
    key: "setContent",
    value: function setContent(_ref3) {
      var asset = _ref3.asset,
        schedule = _ref3.schedule,
        isLive = _ref3.isLive;
      var _ref4 = asset || {},
        vmsId = _ref4.vmsId,
        vrioAssetId = _ref4.vrioAssetId,
        duration = _ref4.duration;
      var _ref5 = schedule || {},
        guideId = _ref5.guideId,
        startTime = _ref5.startTime,
        endTime = _ref5.endTime;
      var durationTime = Math.floor((endTime - startTime) / 1000);
      _classPrivateFieldLooseBase(this, _content)[_content] = {
        vmsId: vmsId,
        vrioAssetId: vrioAssetId,
        duration: isLive ? durationTime : duration,
        isLive: isLive,
        guideId: guideId
      };
    }
  }, {
    key: "updateProgress",
    value: function updateProgress(progress) {
      _classPrivateFieldLooseBase(this, _progress)[_progress] = progress;
    }
  }, {
    key: "updateDuration",
    value: function updateDuration(duration) {
      _classPrivateFieldLooseBase(this, _duration)[_duration] = duration;
    }
  }]);
  return PlaybackHeartbeatController;
}();

/**
 * @typedef Asset
 * @type {object}
 * @property {string} vmsId - The VMS ID.
 * @property {string} vrioAssetId - The VRIO asset ID.
 * @property {number} duration - The duration of the asset in seconds.
 */

/**
 * @typedef Schedule
 * @type {object}
 * @property {string} guideId - The Guide (Schedule) ID.
 * @property {Date} startTime - The start time of the schedule.
 * @property {Date} endTime - The end time of the schedule.
 */
function _getContentType2() {
  return _classPrivateFieldLooseBase(this, _content)[_content].isLive ? ContentType.EPG : ContentType.VOD;
}
function _getDuration2() {
  return Math.ceil(_classPrivateFieldLooseBase(this, _content)[_content].isLive ? _classPrivateFieldLooseBase(this, _content)[_content].duration : _classPrivateFieldLooseBase(this, _duration)[_duration] || _classPrivateFieldLooseBase(this, _content)[_content].duration) || 0;
}
function _getPayload2() {
  return {
    contentType: _classPrivateFieldLooseBase(this, _getContentType)[_getContentType](),
    device: _classPrivateFieldLooseBase(this, _deviceId)[_deviceId],
    duration: _classPrivateFieldLooseBase(this, _getDuration)[_getDuration](),
    sessionId: _classPrivateFieldLooseBase(this, _sessionId)[_sessionId],
    progress: Math.floor(_classPrivateFieldLooseBase(this, _progress)[_progress]),
    streamed: Math.floor(_classPrivateFieldLooseBase(this, _getStreamedTime)[_getStreamedTime]() || 0),
    vmsId: _classPrivateFieldLooseBase(this, _content)[_content].vmsId,
    guideId: _classPrivateFieldLooseBase(this, _content)[_content].guideId,
    vrioAssetId: _classPrivateFieldLooseBase(this, _content)[_content].vrioAssetId
  };
}
function _getPayloadV2() {
  return _objectSpread(_objectSpread({}, _classPrivateFieldLooseBase(this, _getPayload)[_getPayload]()), {}, {
    isStreamingActionSent: _classPrivateFieldLooseBase(this, _isStreamingActionSent)[_isStreamingActionSent]
  });
}
function _send2() {
  return _send3.apply(this, arguments);
}
function _send3() {
  _send3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2() {
    var _this2 = this;
    var playbackStreaming, response;
    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          playbackStreaming = function playbackStreaming() {
            if (_classPrivateFieldLooseBase(_this2, _useStreamingV)[_useStreamingV]) {
              return _classPrivateFieldLooseBase(_this2, _backendService)[_backendService].experience.v4.playbackStreaming(_classPrivateFieldLooseBase(_this2, _getPayloadV)[_getPayloadV]());
            }
            return _classPrivateFieldLooseBase(_this2, _backendService)[_backendService].experience.v3.playbackStreaming(_classPrivateFieldLooseBase(_this2, _getPayload)[_getPayload]());
          };
          _context2.next = 3;
          return playbackStreaming()["catch"](function (error) {
            console.error('[PlaybackHeartbeatController] send', error);
          });
        case 3:
          response = _context2.sent;
          if (_classPrivateFieldLooseBase(this, _useStreamingV)[_useStreamingV] && response !== null && response !== void 0 && response.data.isStreamingActionSent) {
            _classPrivateFieldLooseBase(this, _isStreamingActionSent)[_isStreamingActionSent] = response === null || response === void 0 ? void 0 : response.data.isStreamingActionSent;
          }
          return _context2.abrupt("return", response);
        case 6:
        case "end":
          return _context2.stop();
      }
    }, _callee2, this);
  }));
  return _send3.apply(this, arguments);
}
function _sendStop2() {
  return _classPrivateFieldLooseBase(this, _backendService)[_backendService].experience.v3.playbackStop(_classPrivateFieldLooseBase(this, _getPayload)[_getPayload]())["catch"](function (error) {
    console.error('[PlaybackHeartbeatController] sendStop', error);
  });
}

export { PlaybackHeartbeatController };
//# sourceMappingURL=PlaybackHeartbeatController.js.map
